
import { FC, useState } from "react";
import { Button, Input, Tag } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { RequestFeedbackButton } from "./buttons/request-feedback-button";
import { RequestPaymentButton } from "./buttons/request-payment-button";
import { SendRefundButton } from "./buttons/send-refund-button";
import DOMPurify from "dompurify";

const { TextArea } = Input;
interface MessagesInput {
  sentBy: string;
  message: string;
  embedding: string;
}
 
interface AdhocPaymentRequestInput {
  reason: string | undefined;
  amount: number;
  approval?: {
    isApplicantApprovalRequired: boolean;
  };
}

interface RevisionRequestInput {
  requestedChanges: { [key: string]: boolean };
  revisionSummary: string;
}

interface FinanceDetailsInput {
  transactions: {
    adhocTransactions: [AdhocPaymentRequestInput];
  };
}

interface ChatMessagerProps {
  updateMessage: () => void;
  onSave: (data: ChatMessageSaveInput) => void;
  requestChangesField: { [key: string]: boolean };
  transactions?: any;
  paymentTypes: any;
  caseState: string;
  isAssigned: boolean;
  canIssueRefund: boolean;
}

interface ChatMessageSaveInput {
  messages: [MessagesInput];
  revisionRequest?: RevisionRequestInput;
  financeDetails?: FinanceDetailsInput;
  adhocPayment?: AdhocPaymentRequestInput;
}

export interface Request {
  value: string;
  message: string;
  icon: any;
  amount?: string;
  reason?: string;
  adhocPaymentType?: string;
  [key: string]: any;
}

export const ChatMessager: FC<ChatMessagerProps> = (props) => {
  const [chatMessage, setChatMessage] = useState("");
  const [requests, setRequests] = useState<Request[]>([]);

  const updateEmbedding = (request: Request) => {
    const existingRequest = requests.find(
      (r) =>
        r.value === request.value ||
        (r.value === "sendRefund" && request.value === "requestPayment") ||
        (r.value === "requestPayment" && request.value === "sendRefund")
    );
    if (existingRequest) {
      return;
    }
    setRequests([...requests, request]);
  };

  function repeatEveryMinute() {
    setInterval(props.updateMessage, 60000);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const generateRevisionRequest = (request: Request) => {
    const revisionRequest = Object.keys(props.requestChangesField).reduce(
      (acc: { [x: string]: boolean }, field: string) => {
        acc[field] = request[field] || false;
        return acc;
      },
      {} as Record<string, boolean>
    );

    return {
      requestedChanges: revisionRequest,
      revisionSummary: request.message,
    };
  };

  const sendMessage = async () => {
    if (chatMessage === "") {
      return;
    }
    let embeddedData = [];

    for (const element of requests) {
      if (element.value === "documentRequestType") {
        embeddedData.push({
          revisionRequest: generateRevisionRequest(element),
          value: element.value,
          checked: false,
          completed: false,
        });
      } else if (element.value === "sendRefund") {
        embeddedData.push({
          adhocPayment: {
            reason: element.reason,
            amount: parseFloat(element.amount ?? "") * -1,
          },
          value: element.value,
        });
      } else if (element.value === "requestPayment") {
        embeddedData.push({
          adhocPayment: {
            reason: element.reason,
            amount: parseFloat(element.amount ?? ""),
            adhocPaymentType: element.adhocPaymentType,
          },
          value: element.value,
          completed: false,
          success: false,
        });
      }
    }

    let input: ChatMessageSaveInput = {
      messages: [
        {
          sentBy: "internal",
          message: DOMPurify.sanitize(chatMessage),
          embedding: JSON.stringify(embeddedData),
        },
      ],
    };

    const documentRequest = requests.find(
      (r) => r.value === "documentRequestType"
    );
    if (documentRequest) {
      input.revisionRequest = generateRevisionRequest(documentRequest);
    }

    const paymentRequest = requests.find((r) => r.value === "requestPayment");
    if (paymentRequest) {
      input.financeDetails = {
        transactions: {
          adhocTransactions: [
            {
              reason: paymentRequest.reason,
              amount: parseFloat(paymentRequest.amount ?? ""),
              approval: {
                isApplicantApprovalRequired: true,
              }
            }
          ]
        }
      };
    }

    const refundRequest = requests.find((r) => r.value === "sendRefund");
    if (refundRequest) {
      input.adhocPayment = {
        reason: refundRequest.reason ?? "",
        amount: parseFloat(refundRequest.amount ?? "") * -1,
      };
    }

    props.onSave(input);

    setChatMessage("");
    setRequests([]);
    props.updateMessage();
  };

  const removeRequest = (value: string) => {
    const newRequests = requests.filter((r) => r.value !== value);
    setRequests(newRequests);
  };

  repeatEveryMinute();

  const disableChatButtons = props.caseState === "draft" || props.caseState === "revisionRequested" || props.caseState === "decisionRendered" || props.caseState === "submitted";
  const disableChatTextBox = props.caseState === "draft" || props.caseState === "decisionRendered" || props.caseState === "submitted";

  return (
    <div
      style={{
        gridColumn: 1,
        borderRadius: "8px",
        borderTop: "0px",
        display: "flex",
      }}
    >
      <div
        style={{
          padding: "15px 20px",
          width: "100%",
        }}
      >
        <div
          style={{
            width: "50%",
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "stretch",
          }}
        >
          <RequestFeedbackButton updateEmbedding={updateEmbedding} requestChangesField={props.requestChangesField} disabled={!props.isAssigned || disableChatButtons} />
          <RequestPaymentButton updateEmbedding={updateEmbedding} paymentTypes={props.paymentTypes} disabled={!props.isAssigned || disableChatButtons} />
          <SendRefundButton updateEmbedding={updateEmbedding} transactions={{ submission: props?.transactions?.submission, adhocTransactions: props?.transactions?.adhocTransactions }} disabled={!props.isAssigned || disableChatButtons || !props.canIssueRefund}  />
        </div>
        <div style={{ position: "relative" }}>
          <TextArea
            style={{
              borderRadius: "8px",
              paddingRight: "80px",
              width: "100%",
              padding: "10px",
              paddingBottom: requests.length !== 0 ? "60px" : "10px",
            }}
            onKeyDown={handleKeyDown}
            autoSize={true}
            onChange={(e) => setChatMessage(e.target.value)}
            value={chatMessage}
            placeholder="Type a message..."
            disabled={!props.isAssigned || disableChatTextBox}
          />
          <Button
            style={{
              position: "absolute",
              top: "50%",
              right: "10px",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              paddingLeft: 5,
            }}
            onClick={sendMessage}
            disabled={props.caseState === "draft"}
          >
            <SendOutlined />
          </Button>
          {requests && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                position: "absolute",
                bottom: "10px",
                left: "10px",
              }}
            >
              {requests.map((r) => (
                <Tag
                  key={r.value}
                  closable
                  onClose={() => removeRequest(r.value)}
                >
                  {r.message}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
